import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Paperclip, ArrowUp, X, Square } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import T from 'Components/Helpers/translate';
import AttachmentChip from './AttachmentChip';

type AttachedFile = {
	file: File;
	preview?: string;
};

type Props = {
	onSend: (text: string, files?: File[]) => void;
	disabled?: boolean;
	compact?: boolean;
	placeholder?: string;
	autoFocus?: boolean;
	/** When true the send button becomes a stop button; clicking fires onStop. */
	streaming?: boolean;
	onStop?: () => void;
	/** Fires on every keystroke. Used for slash-command detection on Command home. */
	onChange?: (text: string) => void;
	/** Hard cap on input length. The textarea enforces this and the counter shows once the user is within the last 10%. */
	maxLength?: number;
};

// MIME hints we accept. Any `text/*` type is allowed at validation time too
// (see isAcceptedFile) — the OS picker greys files out by these hints, so the
// extension list below is what actually lets text/code/data files through.
const ACCEPTED_TYPES = [
	'image/*',
	'text/*',
	'application/json',
	'application/xml',
	'application/pdf',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-excel'
];

// Binary formats we accept even though their MIME isn't text/* or image/*.
const BINARY_EXTENSIONS = ['pdf', 'xlsx', 'xls', 'json', 'xml'];

// Plain-text/code extensions that often arrive with an empty or
// application/octet-stream MIME (so the text/* check can't catch them). .sql,
// .py, .sh, .conf, etc. — arbitrary text files.
const TEXT_EXTENSIONS = [
	'txt',
	'text',
	'csv',
	'tsv',
	'md',
	'markdown',
	'log',
	'html',
	'htm',
	'yml',
	'yaml',
	'sql',
	'py',
	'js',
	'jsx',
	'ts',
	'tsx',
	'sh',
	'bash',
	'zsh',
	'rb',
	'go',
	'rs',
	'java',
	'c',
	'h',
	'cpp',
	'cs',
	'php',
	'css',
	'scss',
	'less',
	'ini',
	'conf',
	'cfg',
	'toml',
	'env',
	'properties',
	'diff',
	'patch'
];

const ALL_EXTENSIONS = [...BINARY_EXTENSIONS, ...TEXT_EXTENSIONS];
const EXTENSION_RE = new RegExp(`\\.(${ALL_EXTENSIONS.join('|')})$`, 'i');

const MAX_FILES = 5;
const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
// Interpolated into the size-limit copy so bumping the constant can't leave the
// translations quietly claiming the old number.
const MAX_FILE_LABEL = `${MAX_FILE_MB} MB`;

const isAcceptedFile = (file: File): boolean =>
	file.type.startsWith('image/') || file.type.startsWith('text/') || EXTENSION_RE.test(file.name);

// What the file picker advertises: broad MIME hints plus every extension, so
// text/code files aren't greyed out in the OS dialog.
const ACCEPT_ATTR = [...ACCEPTED_TYPES, ...ALL_EXTENSIONS.map(e => `.${e}`)].join(',');

const ChatInput = ({
	onSend,
	disabled = false,
	compact = false,
	placeholder,
	autoFocus = true,
	streaming = false,
	onStop,
	onChange,
	maxLength
}: Props) => {
	const [text, setText] = useState('');
	const [isFocused, setIsFocused] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);
	const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
	const [attachError, setAttachError] = useState<string | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const el = textareaRef.current;
		if (el) {
			el.style.height = '0';
			el.style.height = el.scrollHeight + 'px';
		}
	}, [text]);

	const addFiles = useCallback(
		(fileList: FileList | File[]) => {
			const newFiles: AttachedFile[] = [];
			// Rejections used to be silent `continue`s, so a file that was the wrong
			// type or too big simply never appeared. Report the first problem only —
			// one file per reason would otherwise stack into a run-on paragraph.
			let error: string | null = null;
			for (const file of Array.from(fileList)) {
				if (attachedFiles.length + newFiles.length >= MAX_FILES) {
					error = error ?? T('assistant.attach.tooMany', { count: MAX_FILES });
					break;
				}
				if (!isAcceptedFile(file)) {
					error = error ?? T('assistant.attach.unsupportedType', { name: file.name });
					continue;
				}
				if (file.size > MAX_FILE_BYTES) {
					error = error ?? T('assistant.attach.tooLarge', { name: file.name, size: MAX_FILE_LABEL });
					continue;
				}
				const attached: AttachedFile = { file };
				if (file.type.startsWith('image/')) attached.preview = URL.createObjectURL(file);
				newFiles.push(attached);
			}
			setAttachError(error);
			if (newFiles.length > 0) setAttachedFiles(prev => [...prev, ...newFiles]);
		},
		[attachedFiles.length]
	);

	const removeFile = useCallback((index: number) => {
		setAttachedFiles(prev => {
			const removed = prev[index];
			if (removed?.preview) URL.revokeObjectURL(removed.preview);
			return prev.filter((_, i) => i !== index);
		});
	}, []);

	// While a reply is streaming the primary action is Stop, not Send (see
	// sendButton below), so the composer is blocked entirely. Without this, the
	// Enter key still fired handleSend, which cleared the text before the parent's
	// own streaming guard silently dropped it — so a message typed mid-reply was
	// lost. Blocking here (and disabling the textarea) keeps the text intact.
	const blocked = disabled || streaming;

	const handleSend = useCallback(() => {
		if ((!text.trim() && attachedFiles.length === 0) || blocked) return;
		const files = attachedFiles.length > 0 ? attachedFiles.map(a => a.file) : undefined;
		onSend(text.trim() || T('assistant.tellMeAboutFiles'), files);
		setText('');
		setAttachError(null);
		for (const a of attachedFiles) {
			if (a.preview) URL.revokeObjectURL(a.preview);
		}
		setAttachedFiles([]);
		requestAnimationFrame(() => textareaRef.current?.focus());
	}, [text, blocked, onSend, attachedFiles]);

	// Refocus the composer the moment the reply finishes so the user can keep
	// typing without clicking back in (the textarea was disabled while streaming).
	const wasStreamingRef = useRef(false);
	useEffect(() => {
		if (wasStreamingRef.current && !streaming) textareaRef.current?.focus();
		wasStreamingRef.current = streaming;
	}, [streaming]);

	const showCounter = maxLength != null && text.length >= maxLength * 0.9;

	const counter = showCounter ? <span className="pr-composer-counter">{`${text.length}/${maxLength}`}</span> : null;

	const sendButton =
		streaming && onStop ? (
			<button type="button" onClick={onStop} className="pr-send-btn" title={T('assistant.stop') || 'Stop'}>
				<Square className="h-3.5 w-3.5 fill-current" />
			</button>
		) : (
			<button
				type="button"
				onClick={handleSend}
				disabled={disabled || (!text.trim() && attachedFiles.length === 0)}
				className="pr-send-btn"
			>
				<ArrowUp className="h-4 w-4" />
			</button>
		);

	const handlePaste = useCallback(
		(e: React.ClipboardEvent) => {
			const items = e.clipboardData?.items;
			if (!items) return;
			const files: File[] = [];
			for (let i = 0; i < items.length; i++) {
				if (items[i].kind === 'file') {
					const file = items[i].getAsFile();
					if (file) files.push(file);
				}
			}
			if (files.length > 0) addFiles(files);
		},
		[addFiles]
	);

	return (
		<div className={compact ? '' : 'pr-composer-wrap'}>
			<div className={compact ? '' : 'pr-composer'}>
				{attachedFiles.length > 0 && (
					<div className="flex gap-2 pb-2 flex-wrap">
						{attachedFiles.map((a, i) => {
							const removeButton = (
								<button
									type="button"
									onClick={() => removeFile(i)}
									className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-grey-13 text-white flex items-center justify-center cursor-pointer z-10"
								>
									<X className="h-2.5 w-2.5" />
								</button>
							);
							if (a.preview) {
								return (
									<div key={i} className="relative w-16 h-16">
										<img
											src={a.preview}
											alt={a.file.name}
											className="w-full h-full object-cover rounded-md border border-border"
										/>
										{removeButton}
									</div>
								);
							}
							// Non-image files use the same chip as the message history
							// (MessageBlock) so "just uploaded" matches "in the chat".
							return (
								<div key={i} className="relative">
									<AttachmentChip name={a.file.name} mimeType={a.file.type} sizeBytes={a.file.size} />
									{removeButton}
								</div>
							);
						})}
					</div>
				)}

				<div
					className={cn(
						'pr-composer-box',
						compact && 'compact',
						isFocused && 'focused',
						isDragOver && 'drag'
					)}
					onDragOver={e => {
						e.preventDefault();
						e.stopPropagation();
						setIsDragOver(true);
					}}
					onDragLeave={e => {
						e.preventDefault();
						e.stopPropagation();
						setIsDragOver(false);
					}}
					onDrop={e => {
						e.preventDefault();
						e.stopPropagation();
						setIsDragOver(false);
						if (e.dataTransfer.files?.length) {
							addFiles(e.dataTransfer.files);
							return;
						}
						// Slack (and anything else that hands over a link rather than a
						// file) puts only string data on the drag, so there is nothing to
						// read and a URL can't be fetched back from here. Only say so for
						// link-shaped drags — dragging a plain text selection is a
						// different gesture and "use the paperclip" is wrong advice.
						if (e.dataTransfer.types?.includes('text/uri-list')) {
							setAttachError(T('assistant.attach.noFileInDrop'));
						}
					}}
				>
					{isDragOver && (
						<div className="absolute inset-0 rounded-[14px] flex items-center justify-center pointer-events-none text-body-sm text-muted-foreground">
							{T('assistant.dropFilesHere')}
						</div>
					)}

					<input
						ref={fileInputRef}
						type="file"
						multiple
						accept={ACCEPT_ATTR}
						className="hidden"
						onChange={e => {
							if (e.target.files) addFiles(e.target.files);
							e.target.value = '';
						}}
					/>

					{compact && (
						<button
							type="button"
							onClick={() => fileInputRef.current?.click()}
							disabled={blocked || attachedFiles.length >= MAX_FILES}
							className="pr-composer-tool"
						>
							<Paperclip className="h-4 w-4" />
						</button>
					)}

					<textarea
						ref={textareaRef}
						value={text}
						disabled={blocked}
						onChange={e => {
							setText(e.target.value);
							onChange?.(e.target.value);
						}}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						onPaste={handlePaste}
						onKeyDown={e => {
							if (e.key === 'Enter' && (text.trim() || attachedFiles.length > 0) && !e.shiftKey) {
								e.preventDefault();
								handleSend();
							}
						}}
						placeholder={placeholder ?? T('assistant.placeholder')}
						rows={1}
						autoFocus={autoFocus}
						maxLength={maxLength}
						className="pr-composer-textarea"
					/>

					{compact ? (
						<>
							{counter}
							{sendButton}
						</>
					) : (
						<div className="pr-composer-actions">
							<div className="pr-composer-actions-left">
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									disabled={blocked || attachedFiles.length >= MAX_FILES}
									className="pr-composer-tool"
									title={T('assistant.attachFile') || 'Attach file'}
								>
									<Paperclip className="h-4 w-4" />
								</button>
							</div>
							<div className="pr-composer-actions-right">
								{counter}
								{sendButton}
							</div>
						</div>
					)}
				</div>

				{attachError && (
					<div className="pr-composer-attach-error" role="alert">
						{attachError}
					</div>
				)}
			</div>
		</div>
	);
};

export default ChatInput;
